// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useMemo, useState } from "react";

import { mdiRestore } from "@mdi/js";
import { Button, Input, TreeSelect } from "antd";
import {
  type OctantLengthOverride,
  type OctantLengthOverrides,
  type OctantLengthUnit,
  SPACING_MAX_RADIUS_MIN // PARKED (EVA-163 comment 163139) — needed only by the four octant parameter
  // controls and the zone colouring, both of which are commented out below.
  // HOR_CUTOFF_ANGLE_DEG,
  // OCTANT_BAND_MIN,
  // OCTANT_CUTOFF_ANGLE_MAX,
  // OCTANT_CUTOFF_ANGLE_MIN,
  // OCTANT_ZONE_COLORS,
  // VERT_CUTOFF_ANGLE_DEG
} from "constants/octantZone.constants";
import styled from "styled-components/macro";

import { useLengthConversion } from "hooks";

import { useXdaSettingsStore } from "../../context";
import {
  selectOctantLengthOverrides,
  selectSpacingClassFilter,
  selectUpdateXdaSettings // PARKED — the two cutoff angles are no longer exposed.
  // selectOctantHorCutoffDeg,
  // selectOctantVertCutoffDeg
} from "../../context/xdaSettingsSelectors";
import {
  OCTANT_ZONES,
  resolveSpacingMaxRadii,
  resolveVisibleZoneList // PARKED — only the removed parameter controls read the resolved thresholds.
  // OctantZone,
  // resolveOctantThresholds
} from "../../util/octantZone";
import { XdaOptionItem, XdaOptionsWrapper } from "./XdaOptionsLayout";
import "./XdaSpacingOptions.scss";

// Spacing Class controls (EVA-163).
//
// SCOPE REDUCED per Travis's revisions (EVA-163 comment 163139): the panel now
// carries the Class Filter and the two neighbour caps only. The four octant
// parameter controls — Hor Max of Stack, Vert Max of Lateral, Vert Cutoff
// Angle, Hor Cutoff Angle — and every zone colour are COMMENTED OUT rather
// than deleted, because they may come back depending on user input. The
// classifier itself is untouched: it still resolves those four parameters from
// their defaults, they are simply no longer editable here.
//
// The commented-out blocks below are kept verbatim so restoring them is an
// uncomment rather than a rewrite. Each is marked PARKED.
//
// PARKED — what the four controls were for:
//   Hor Max of Stack     — the stack band's width          → Stack red
//   Vert Cutoff Angle    — how far off vertical is a Stack → Stack red
//   Vert Max of Lateral  — the lateral band's height       → Lateral blue
//   Hor Cutoff Angle     — how far off horizontal a Lateral → Lateral blue
// Stagger had no control: it is the residual class, everything the other two
// do not claim, so there was nothing to parameterise.
//
// UNITS: the caps are read and written in the scene's DISPLAY unit and are
// never passed through `convert`/`convertBack`. The XDA backend already
// returns the scene in display units, so a conversion here would be the
// EVA-6715 double-conversion bug. The store keys the overrides by that unit.

/* PARKED (EVA-163 comment 163139) — the "All classes" collapse. The filter now
   shows the three zones as separate tags when everything is selected, rather
   than folding them into one "All classes" tag via a hidden parent node.
const ALL_CLASSES_KEY = "__all_classes__";
// Shared with XdaSpacingOptions.scss, which hides the row this lands on.
const ALL_CLASSES_NODE_CLASS = "xda-spacing-all-node";

// Which parameters each zone's color is attached to (see the table above).
const STACK_COLOR = OCTANT_ZONE_COLORS[OctantZone.Stack];
const LATERAL_COLOR = OCTANT_ZONE_COLORS[OctantZone.Lateral];
*/

type BandKey = keyof OctantLengthOverride;

const DROPDOWN_STYLE = { minWidth: 200, maxHeight: 320, overflow: "auto" };

const XdaSpacingOptions = () => {
  const spacingClassFilter = useXdaSettingsStore(selectSpacingClassFilter);
  const octantLengthOverrides = useXdaSettingsStore(selectOctantLengthOverrides);
  const updateXdaSettings = useXdaSettingsStore(selectUpdateXdaSettings);
  /* PARKED — read only by the removed cutoff-angle controls.
  const octantHorCutoffDeg = useXdaSettingsStore(selectOctantHorCutoffDeg);
  const octantVertCutoffDeg = useXdaSettingsStore(selectOctantVertCutoffDeg);
  */

  const { getDisplayUnit } = useLengthConversion();
  const displayUnit = getDisplayUnit();
  const unit: OctantLengthUnit = displayUnit === "ft" ? "ft" : "m";

  /* PARKED — what the classifier will actually use. Reading the inputs off the
     SAME resolver the overlay draws with is what made an out-of-range or
     cleared value snap back to the number really in force.
  const thresholds = useMemo(
    () =>
      resolveOctantThresholds(displayUnit, {
        lengths: octantLengthOverrides,
        horCutoffDeg: octantHorCutoffDeg,
        vertCutoffDeg: octantVertCutoffDeg
      }),
    [displayUnit, octantLengthOverrides, octantHorCutoffDeg, octantVertCutoffDeg]
  );
  */

  // Read the caps back through the resolver so an unset one shows as blank
  // ("no limit") rather than as a made-up number.
  const maxRadii = useMemo(
    () => resolveSpacingMaxRadii(displayUnit, octantLengthOverrides),
    [displayUnit, octantLengthOverrides]
  );

  // In-progress text, keyed by control. An input is uncontrolled-ish only
  // while it is being edited: "" and "-" and "4." are all states a user passes
  // through on the way to a valid number, and writing those to the store would
  // either reset the parameter or fight the keystrokes. Cleared on blur, which
  // is what snaps the field back to the resolved value.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  /* PARKED — the plain reader, used by the four removed parameter inputs.
  const displayed = (key: string, effective: number) => drafts[key] ?? String(effective);
  */
  // Infinity has no sensible spelling in a number input, so "no limit" is the
  // empty string plus the placeholder.
  const cappedValue = (key: string, effective: number) =>
    drafts[key] ?? (Number.isFinite(effective) ? String(effective) : "");
  // Drafts are keyed by parameter, NOT by unit, so a half-typed value would
  // otherwise survive a unit-system switch and commit under the other unit's
  // key — the one thing the per-unit storage exists to prevent ("a band the
  // user typed in feet is only ever read back as feet"). The displayed values
  // already re-resolve through the resolver; this stops the in-progress text
  // riding along with them.
  useEffect(() => {
    setDrafts({});
  }, [unit]);

  const clearDraft = (key: string) =>
    setDrafts((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });

  const writeBand = (key: BandKey, value: Maybe<number>) => {
    const forUnit: OctantLengthOverride = { ...(octantLengthOverrides?.[unit] ?? {}) };
    if (value == null) {
      delete forUnit[key];
    } else {
      forUnit[key] = value;
    }
    const next: OctantLengthOverrides = { ...(octantLengthOverrides ?? {}) };
    // An emptied unit is deleted rather than left as `{}`, so "no overrides"
    // has exactly one representation in a saved dashboard.
    if (Object.keys(forUnit).length) {
      next[unit] = forUnit;
    } else {
      delete next[unit];
    }
    updateXdaSettings({ octantLengthOverrides: next });
  };

  // Emptying a length input removes the override, which for a cap restores "no
  // limit" — rather than storing a 0, which would mean a plot with no
  // neighbours at all.
  const handleLengthChange = (key: BandKey, min: number) => (raw: string) => {
    setDrafts((current) => ({ ...current, [key]: raw }));
    if (raw.trim() === "") {
      writeBand(key, undefined);
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= min) {
      writeBand(key, parsed);
    }
  };
  const handleCapChange = (key: BandKey) =>
    handleLengthChange(key, SPACING_MAX_RADIUS_MIN);
  /* PARKED — the band variant differed only in its floor (0 rather than 1),
     because a band of 0 is meaningful where a cap of 0 is not.
  const handleBandChange = (key: BandKey) => handleLengthChange(key, OCTANT_BAND_MIN);

  const handleAngleChange =
    (key: "octantHorCutoffDeg" | "octantVertCutoffDeg", fallback: number) =>
    (raw: string) => {
      setDrafts((current) => ({ ...current, [key]: raw }));
      // Emptying an angle means what emptying a band or a cap means: put the
      // parameter back to its default.
      if (raw.trim() === "") {
        updateXdaSettings({ [key]: fallback });
        return;
      }
      const parsed = Number(raw);
      if (
        Number.isFinite(parsed) &&
        parsed >= OCTANT_CUTOFF_ANGLE_MIN &&
        parsed <= OCTANT_CUTOFF_ANGLE_MAX
      ) {
        updateXdaSettings({ [key]: parsed });
      }
    };
  */

  // A parameter counts as untouched when no override is recorded for it AND
  // nothing is half-typed in its field; the reset is disabled in that state so
  // it reads as "nothing to undo" rather than as a button that does nothing.
  //
  // The draft half matters: a rejected entry never reaches the store, so
  // without it the field would sit there showing "-5" with its own reset
  // greyed out — the one moment the user most wants it.
  const hasDraft = (key: string) => drafts[key] !== undefined;
  const lengthIsDefault = (key: BandKey) =>
    octantLengthOverrides?.[unit]?.[key] === undefined && !hasDraft(key);

  /* PARKED — against the RESOLVED angle, never the raw store field: a widget
     saved before EVA-163 holds `undefined` there, and `undefined === 45` is
     false, which rendered both angle resets enabled on every legacy dashboard.
  const angleIsDefault = (
    key: "octantHorCutoffDeg" | "octantVertCutoffDeg",
    fallback: number
  ) =>
    (key === "octantHorCutoffDeg"
      ? thresholds.horCutoffDeg
      : thresholds.vertCutoffDeg) === fallback && !hasDraft(key);
  */

  // Restore one parameter to what it would be with no override at all — for a
  // cap, "no limit". Same path as clearing the field by hand, so there is
  // exactly one way for a parameter to be un-set.
  const resetLength = (key: BandKey) => () => {
    clearDraft(key);
    writeBand(key, undefined);
  };
  /* PARKED — the angle variant wrote the Model Engine default back rather than
     removing an override, angles having no per-unit record.
  const resetAngle =
    (key: "octantHorCutoffDeg" | "octantVertCutoffDeg", fallback: number) => () => {
      clearDraft(key);
      updateXdaSettings({ [key]: fallback });
    };
  */

  // Checked state comes from the resolver, so the "empty means every class"
  // rule has exactly one implementation: an unfiltered widget shows all three
  // boxes ticked, and all three as separate tags (EVA-163 comment 163139 —
  // Travis asked to see the classes rather than an "All classes" summary).
  const checkedZones = useMemo(
    () => resolveVisibleZoneList(spacingClassFilter),
    [spacingClassFilter]
  );

  // Flat: three zones, no parent. The colour square that used to sit beside
  // each name is PARKED with the rest of the zone colouring — with the legend
  // gone and every label black, a coloured square here would promise a colour
  // coding that appears nowhere else.
  const treeData = useMemo(
    () =>
      OCTANT_ZONES.map((zone) => ({
        title: zone,
        value: zone,
        key: zone
      })),
    []
  );
  /* PARKED — the "All classes" parent, hidden as a row and used only so
     SHOW_PARENT could collapse a full selection into a single tag.
  const treeData = useMemo(
    () => [
      {
        className: ALL_CLASSES_NODE_CLASS,
        title: "All classes",
        value: ALL_CLASSES_KEY,
        key: ALL_CLASSES_KEY,
        children: OCTANT_ZONES.map((zone) => ({
          title: (
            <ZoneOption>
              <ZoneSwatch color={OCTANT_ZONE_COLORS[zone]} />
              {zone}
            </ZoneOption>
          ),
          value: zone,
          key: zone
        }))
      }
    ],
    []
  );
  */

  const handleClassFilterChange = (values: string[]) => {
    const selected = OCTANT_ZONES.filter((zone) => values.includes(zone));
    // Every class selected — and none selected, which is what clearing the
    // control does — are both stored as the empty filter, the one canonical
    // "unfiltered" value. Unticking the last box therefore returns to all
    // classes rather than emptying the plot: a spacing display with no classes
    // shows no distances at all, which is never what the control is for.
    const isUnfiltered = selected.length === 0 || selected.length === OCTANT_ZONES.length;
    updateXdaSettings({ spacingClassFilter: isUnfiltered ? [] : selected });
  };

  return (
    <XdaOptionsWrapper
      width={"380px"}
      paddingRight={"12px"}
      cardBodyPadding={"5px 5px 5px 5px"}>
      <XdaOptionItem>
        <SpacingOptionLabel>Class Filter</SpacingOptionLabel>
        {/* No maxTagCount: the default state is three tags and all of them
            should be readable rather than collapsed to a "+2". */}
        <ClassFilterSelect
          data-testid="xda-spacing-class-filter"
          size="small"
          treeCheckable
          treeDefaultExpandAll
          // The popup is NOT tied to the control's width: at 160px every zone
          // name truncates to "Lat…".
          dropdownMatchSelectWidth={false}
          dropdownStyle={DROPDOWN_STYLE}
          popupClassName="xda-spacing-class-dropdown"
          treeData={treeData}
          value={checkedZones}
          onChange={handleClassFilterChange}
          /* PARKED — unreachable, and it names the concept Travis asked to
             remove: the empty filter resolves to all three zones, so the value
             is never empty and this never renders.
          placeholder="All classes"
          */
          /* PARKED — collapsed a full selection to the hidden parent's tag.
          showCheckedStrategy={TreeSelect.SHOW_PARENT}
          */
        />
      </XdaOptionItem>

      {/* PARKED (EVA-163 comment 163139) — the four octant parameter controls.
          The classifier still uses these four values; they are simply no longer
          editable from the panel.

      <XdaOptionItem>
        <SpacingOptionLabel>Horizontal Max of Stack ({displayUnit})</SpacingOptionLabel>
        <ZoneField>
          <SwatchSlot>
            <ZoneSwatch data-testid="xda-spacing-swatch-hmax-stack" color={STACK_COLOR} />
          </SwatchSlot>
          <PlainInput
            data-testid="xda-spacing-hmax-stack"
            min={OCTANT_BAND_MIN}
            onBlur={() => clearDraft("hMaxStack")}
            onChange={(e) => handleBandChange("hMaxStack")(e.target.value)}
            size="small"
            step={10}
            type="number"
            value={displayed("hMaxStack", thresholds.hMaxStack)}
          />
          <ResetButton
            aria-label="Reset Horizontal Max of Stack"
            data-testid="xda-spacing-reset-hmax-stack"
            disabled={lengthIsDefault("hMaxStack")}
            icon={<Icon path={mdiRestore} size={0.75} />}
            onClick={resetLength("hMaxStack")}
            size="small"
            title="Reset to default"
            type="text"
          />
        </ZoneField>
      </XdaOptionItem>

      <XdaOptionItem>
        <SpacingOptionLabel>Vertical Max of Lateral ({displayUnit})</SpacingOptionLabel>
        <ZoneField>
          <SwatchSlot>
            <ZoneSwatch
              data-testid="xda-spacing-swatch-vmax-lateral"
              color={LATERAL_COLOR}
            />
          </SwatchSlot>
          <PlainInput
            data-testid="xda-spacing-vmax-lateral"
            min={OCTANT_BAND_MIN}
            onBlur={() => clearDraft("vMaxLateral")}
            onChange={(e) => handleBandChange("vMaxLateral")(e.target.value)}
            size="small"
            step={10}
            type="number"
            value={displayed("vMaxLateral", thresholds.vMaxLateral)}
          />
          <ResetButton
            aria-label="Reset Vertical Max of Lateral"
            data-testid="xda-spacing-reset-vmax-lateral"
            disabled={lengthIsDefault("vMaxLateral")}
            icon={<Icon path={mdiRestore} size={0.75} />}
            onClick={resetLength("vMaxLateral")}
            size="small"
            title="Reset to default"
            type="text"
          />
        </ZoneField>
      </XdaOptionItem>

      <SmallPadding />

      <XdaOptionItem>
        <SpacingOptionLabel>Vertical Cutoff Angle (°)</SpacingOptionLabel>
        <ZoneField>
          <SwatchSlot>
            <ZoneSwatch data-testid="xda-spacing-swatch-vert-cutoff" color={STACK_COLOR} />
          </SwatchSlot>
          <PlainInput
            data-testid="xda-spacing-vert-cutoff"
            max={OCTANT_CUTOFF_ANGLE_MAX}
            min={OCTANT_CUTOFF_ANGLE_MIN}
            onBlur={() => clearDraft("octantVertCutoffDeg")}
            onChange={(e) =>
              handleAngleChange("octantVertCutoffDeg", VERT_CUTOFF_ANGLE_DEG)(e.target.value)
            }
            size="small"
            step={1}
            type="number"
            value={displayed("octantVertCutoffDeg", thresholds.vertCutoffDeg)}
          />
          <ResetButton
            aria-label="Reset Vertical Cutoff Angle"
            data-testid="xda-spacing-reset-vert-cutoff"
            disabled={angleIsDefault("octantVertCutoffDeg", VERT_CUTOFF_ANGLE_DEG)}
            icon={<Icon path={mdiRestore} size={0.75} />}
            onClick={resetAngle("octantVertCutoffDeg", VERT_CUTOFF_ANGLE_DEG)}
            size="small"
            title="Reset to default"
            type="text"
          />
        </ZoneField>
      </XdaOptionItem>

      <XdaOptionItem>
        <SpacingOptionLabel>Horizontal Cutoff Angle (°)</SpacingOptionLabel>
        <ZoneField>
          <SwatchSlot>
            <ZoneSwatch data-testid="xda-spacing-swatch-hor-cutoff" color={LATERAL_COLOR} />
          </SwatchSlot>
          <PlainInput
            data-testid="xda-spacing-hor-cutoff"
            max={OCTANT_CUTOFF_ANGLE_MAX}
            min={OCTANT_CUTOFF_ANGLE_MIN}
            onBlur={() => clearDraft("octantHorCutoffDeg")}
            onChange={(e) =>
              handleAngleChange("octantHorCutoffDeg", HOR_CUTOFF_ANGLE_DEG)(e.target.value)
            }
            size="small"
            step={1}
            type="number"
            value={displayed("octantHorCutoffDeg", thresholds.horCutoffDeg)}
          />
          <ResetButton
            aria-label="Reset Horizontal Cutoff Angle"
            data-testid="xda-spacing-reset-hor-cutoff"
            disabled={angleIsDefault("octantHorCutoffDeg", HOR_CUTOFF_ANGLE_DEG)}
            icon={<Icon path={mdiRestore} size={0.75} />}
            onClick={resetAngle("octantHorCutoffDeg", HOR_CUTOFF_ANGLE_DEG)}
            size="small"
            title="Reset to default"
            type="text"
          />
        </ZoneField>
      </XdaOptionItem>
      */}

      <SmallPadding />

      <XdaOptionItem>
        <SpacingOptionLabel>Max Neighbour Radius ({displayUnit})</SpacingOptionLabel>
        <ZoneField>
          <PlainInput
            data-testid="xda-spacing-max-neighbour"
            min={SPACING_MAX_RADIUS_MIN}
            onBlur={() => clearDraft("maxNeighborRadius")}
            onChange={(e) => handleCapChange("maxNeighborRadius")(e.target.value)}
            placeholder="No limit"
            size="small"
            step={10}
            type="number"
            value={cappedValue("maxNeighborRadius", maxRadii.neighbor)}
          />
          <ResetButton
            aria-label="Reset Max Neighbour Radius"
            data-testid="xda-spacing-reset-max-neighbour"
            disabled={lengthIsDefault("maxNeighborRadius")}
            icon={<Icon path={mdiRestore} size={0.75} />}
            onClick={resetLength("maxNeighborRadius")}
            size="small"
            title="Reset to default"
            type="text"
          />
        </ZoneField>
      </XdaOptionItem>

      <XdaOptionItem>
        <SpacingOptionLabel>Max Vertical Distance ({displayUnit})</SpacingOptionLabel>
        <ZoneField>
          <PlainInput
            data-testid="xda-spacing-max-vertical"
            min={SPACING_MAX_RADIUS_MIN}
            onBlur={() => clearDraft("maxVerticalDistance")}
            onChange={(e) => handleCapChange("maxVerticalDistance")(e.target.value)}
            placeholder="No limit"
            size="small"
            step={10}
            type="number"
            value={cappedValue("maxVerticalDistance", maxRadii.vertical)}
          />
          <ResetButton
            aria-label="Reset Max Vertical Distance"
            data-testid="xda-spacing-reset-max-vertical"
            disabled={lengthIsDefault("maxVerticalDistance")}
            icon={<Icon path={mdiRestore} size={0.75} />}
            onClick={resetLength("maxVerticalDistance")}
            size="small"
            title="Reset to default"
            type="text"
          />
        </ZoneField>
      </XdaOptionItem>

      <HintText>
        Distances are measured from the reference well. Click any well in the plot to
        re-centre the classification.
      </HintText>
    </XdaOptionsWrapper>
  );
};

export default XdaSpacingOptions;

// Wider than the shared XdaOptionLabel: these labels carry both the parameter
// name and its unit, and wrapping "Max Neighbour Radius (ft)" onto two lines
// misaligns every row in the panel.
const SpacingOptionLabel = styled.label`
  min-width: 170px;
`;

// Quiet by design: the reset is a recovery affordance, not a primary action,
// so it stays a borderless glyph that only gains colour on hover.
const ResetButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* The glyph itself is 18px (mdi size 0.75 = 0.75 x 1.5rem), inside a 24px
       hit target — comfortably past the 16px floor a 22px button was crowding. */
    width: 24px;
    height: 24px;
    min-width: 24px;
    padding: 0;
    border: none;
    color: var(--color-text-70, #75808a);
  }

  &&:hover:not(:disabled) {
    color: var(--color-primary);
    background: transparent;
  }

  &&:disabled {
    opacity: 0.35;
  }
`;

const PlainInput = styled(Input)`
  max-width: 110px;
  border-radius: var(--border-radius);
`;

// `&&` to out-specify XdaOptionItem's `.ant-select { width: 130px }`, which is
// sized for the plain Selects in the sibling panels rather than a tag control.
const ClassFilterSelect = styled(TreeSelect)`
  && {
    width: 160px;
    min-width: 160px;
  }

  /* TWO rows: 2 x (20px tag + 4px margins) + 2px padding + 2px border.
     Two rows is both the most this control ever needs and enough to stop it
     jumping. The full three-zone default does NOT take three rows — at this
     width "Lateral" and "Stack" share the first row and "Stagger" drops to the
     second — so three tags fit in two, and unticking down to two or one still
     occupies at most two. Sized for three it carried a permanently empty row. */
  && .ant-select-selector {
    height: auto;
    min-height: 52px;
    align-items: flex-start;
    padding-top: 2px;
  }

  /* antd's small-size tag is 16px tall with a 14px line-height and
     overflow:hidden, which cut the descenders off "Stagger". */
  && .ant-select-selection-item {
    height: 20px;
    line-height: 18px;
  }
`;

// Input + reset as one right-aligned unit.
const ZoneField = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* PARKED (EVA-163 comment 163139) — the zone colouring. Every label is black
   now and the legend is gone, so a coloured square here would reference a
   colour coding that appears nowhere else in the widget.
const ZoneOption = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  // Zone names are short and must never wrap or truncate.
  white-space: nowrap;
`;

const ZoneSwatch = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${(p) => p.color};
`;

// Always rendered, even for the uncoloured caps, so the six inputs shared one
// column rather than the capped pair sitting 20px further right.
const SwatchSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 12px;
  width: 12px;
`;
*/

const HintText = styled.p`
  margin: 8px 0 0;
  color: var(--color-text-second, #5c6670);
  font-size: 1.1rem;
  line-height: 1.4;
`;

const SmallPadding = styled.div`
  padding-top: 4px;
`;
